import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import LoadingScreen from '@/components/ui/LoadingScreen'
import { useTranslation } from 'react-i18next'

interface AuthGuardProps {
  children: React.ReactNode
}

/**
 * 데모(솔루션 베타) 전용 워터마크 — 백엔드가 admin.role === 'viewer' 를 줄 때만 표시.
 * 운영 백엔드는 role 필드가 없어 절대 렌더링되지 않음(크리조닉 데모 서버 전용).
 */
const DemoWatermark = () => (
  <div
    aria-hidden
    style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99999, pointerEvents: 'none', overflow: 'hidden' }}
  >
    <div
      style={{
        position: 'absolute', top: '-100%', left: '-50%', width: '300%', height: '400%',
        display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start',
        transform: 'rotate(-35deg)', gap: '60px 80px', padding: '40px',
      }}
    >
      {Array.from({ length: 500 }).map((_, i) => (
        <span
          key={i}
          style={{ fontSize: '22px', fontWeight: 700, color: 'rgba(255, 255, 255, 0.07)', whiteSpace: 'nowrap', userSelect: 'none', letterSpacing: '2px' }}
        >
          테스트 페이지입니다.
        </span>
      ))}
    </div>
  </div>
)

const AuthGuard = ({ children }: AuthGuardProps) => {
  const { t } = useTranslation(['auth', 'common'])
  const { initializeAuth, admin, isAuthenticated, isLoading } = useAuthStore()
  const [isChecking, setIsChecking] = useState(true)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        await initializeAuth()
        // admin 정보가 로드될 때까지 약간의 대기 (상태 업데이트 반영)
        await new Promise(resolve => setTimeout(resolve, 200))
        
        // isAuthenticated가 true인데 admin이 null인 경우 (localStorage 불일치)
        // 최대 2번까지 재시도
        if (mounted && isAuthenticated && !admin && retryCount < 2) {
          console.warn('🔔 AuthGuard: admin 정보 불일치 감지, 재시도 중...', { isAuthenticated, admin, retryCount })
          setRetryCount(prev => prev + 1)
          await initializeAuth()
          await new Promise(resolve => setTimeout(resolve, 200))
        }
      } finally {
        if (mounted) setIsChecking(false)
      }
    })()
    return () => { mounted = false }
  }, [initializeAuth, retryCount])

  // 초기 체크 중이거나 로딩 중일 때 로딩 화면
  if (isChecking || isLoading) {
    return <LoadingScreen />
  }

  // 토큰이 없으면 로그인 페이지로 리다이렉트
  const hasToken = !!localStorage.getItem('admin_token')
  if (!hasToken) {
    return <Navigate to="/login" replace />
  }

  // isAuthenticated가 true인데 admin이 null인 경우 (localStorage 불일치)
  // localStorage를 정리하고 로그인 페이지로 리다이렉트
  if (isAuthenticated && !admin) {
    console.error('🔔 AuthGuard: admin 정보 불일치 - localStorage 정리 후 로그인 페이지로 이동', { isAuthenticated, admin })
    // localStorage 정리
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin-auth')
    // authStore 초기화
    useAuthStore.getState().logout()
    return <Navigate to="/login" replace />
  }

  // admin 정보가 없고 인증되지 않았으면 로그인 페이지로 리다이렉트
  if (!isAuthenticated || !admin) {
    return <Navigate to="/login" replace />
  }

  const isViewer = (admin as any)?.role === 'viewer'

  return (
    <>
      {isViewer && <DemoWatermark />}
      {children}
    </>
  )
}

export default AuthGuard